import type { TaskResponse } from '@erp/rpc';
import { isTerminalTaskStatus } from '@erp/shared-types';
import type { RowSelectionState, Updater } from '@/lib/tanstack-table';

// Pure selection logic for the tasks-queue bulk actions, kept free of React
// imports so it stays trivially unit-testable (mirrors taskQueueSearch).

/**
 * Row selection pinned to the query scope (page, filters, sort, search) it
 * was made in. The bulk action only acts on rows of the current page, so a
 * selection from a previous scope must not linger invisibly and then be
 * wiped by an unrelated action; it is simply dropped when the scope changes.
 */
export type ScopedRowSelection = {
  scopeKey: string;
  rows: RowSelectionState;
};

const NO_ROWS: RowSelectionState = {};

export function emptyScopedSelection(scopeKey: string): ScopedRowSelection {
  return { scopeKey, rows: NO_ROWS };
}

/** The rows selected in `scopeKey`; nothing if the selection is from another scope. */
export function rowsInScope(
  selection: ScopedRowSelection,
  scopeKey: string,
): RowSelectionState {
  return selection.scopeKey === scopeKey ? selection.rows : NO_ROWS;
}

/**
 * Apply a TanStack row-selection update on top of whatever is selected in
 * `scopeKey` (a stale selection counts as empty), re-pinning the result.
 */
export function updateScopedSelection(
  previous: ScopedRowSelection,
  scopeKey: string,
  update: Updater<RowSelectionState>,
): ScopedRowSelection {
  const base = rowsInScope(previous, scopeKey);
  const rows = typeof update === 'function' ? update(base) : update;
  return { scopeKey, rows };
}

/** TanStack removes deselected keys, so every remaining key is a selected row. */
export function selectedIdsOf(rows: RowSelectionState): Set<string> {
  return new Set(Object.keys(rows));
}

export function visibleSelectedTaskIds(
  tasks: readonly TaskResponse[],
  selectedIds: ReadonlySet<string>,
): string[] {
  const ids: string[] = [];
  for (const task of tasks) {
    if (selectedIds.has(task.id)) ids.push(task.id);
  }
  return ids;
}

/**
 * Statuses a bulk "Return to Agent" acts on: the two ways an agent hands work
 * back — a hard failure, or an escalation waiting on a human. Open work
 * (`pending` / `in_progress`) and deliberate closes (`completed` /
 * `cancelled`) are left alone even though the server would accept the
 * transition, so a broad selection can't re-queue finished work.
 */
const RETURNABLE_STATUSES: ReadonlySet<TaskResponse['status']> = new Set([
  'failed',
  'needs_input',
]);

/**
 * The agent a task returns to, mirroring the server's resolution: the current
 * agent assignee, else the agent it was escalated away from (a `needs_input`
 * hand-off reassigns the task to a user and leaves the agent only in metadata).
 */
export function returnAgentIdForTask(task: TaskResponse): string | null {
  if (task.assigneeAgentDefinitionId) return task.assigneeAgentDefinitionId;
  const escalatedFrom = task.metadata?.escalatedFromAgentDefinitionId;
  return typeof escalatedFrom === 'string' ? escalatedFrom : null;
}

export function canReturnTaskToAgent(task: TaskResponse): boolean {
  return (
    RETURNABLE_STATUSES.has(task.status) && returnAgentIdForTask(task) !== null
  );
}

/**
 * Split the selected rows (by id, in row order) into the ones the bulk action
 * will send and the ones it skips, so the bar can show both counts.
 */
export function partitionReturnableTasks(
  tasks: readonly TaskResponse[],
  selectedIds: ReadonlySet<string>,
): { returnable: TaskResponse[]; skipped: TaskResponse[] } {
  const returnable: TaskResponse[] = [];
  const skipped: TaskResponse[] = [];
  for (const task of tasks) {
    if (!selectedIds.has(task.id)) continue;
    if (canReturnTaskToAgent(task)) returnable.push(task);
    else skipped.push(task);
  }
  return { returnable, skipped };
}

/**
 * The selected rows (in row order) a "return to a different agent" acts on:
 * the same failed / needs_input statuses as "Return to Agent", but no prior
 * agent is required because the user picks the target agent.
 */
export function otherAgentReturnableTasks(
  tasks: readonly TaskResponse[],
  selectedIds: ReadonlySet<string>,
): TaskResponse[] {
  return tasks.filter(
    (task) => selectedIds.has(task.id) && RETURNABLE_STATUSES.has(task.status),
  );
}

/**
 * Split the selected rows into ones a mass-reassign will offer and the closed
 * ones it leaves alone. Status is not changed, so a finished task keeps the
 * assignee it closed with.
 */
export function partitionReassignableTasks(
  tasks: readonly TaskResponse[],
  selectedIds: ReadonlySet<string>,
): { reassignable: TaskResponse[]; skipped: TaskResponse[] } {
  const reassignable: TaskResponse[] = [];
  const skipped: TaskResponse[] = [];
  for (const task of tasks) {
    if (!selectedIds.has(task.id)) continue;
    if (isTerminalTaskStatus(task.status)) skipped.push(task);
    else reassignable.push(task);
  }
  return { reassignable, skipped };
}
